import { DEFAULT_INDEX_GROUP } from '@/config/constants';
import type { OtherGroupIndexEntry } from '@/entities/market';
import { symbolKey } from '@/entities/symbol';
import type {
  GroupIndexDataRecord,
  GroupIndexSymbol
} from '@/features/markets/api/groupIndex.queries';
import {
  calculateDelta,
  collectFetchableSymbols,
  priceValue,
  useEnrichedSections
} from '@/features/markets/api/groupIndex.queries';

import type {
  CommoditiesTableRowType,
  CommoditiesTableSectionType
} from '@/features/markets/types';

type RowType = CommoditiesTableSectionType['rows'][number];

const getRowSymbols = (row: RowType): GroupIndexSymbol[] => [row.symbol, row.diffSymbol];

function enrichFromData(
  data: OtherGroupIndexEntry[],
  role: 'symbol' | 'diffSymbol'
): Partial<CommoditiesTableRowType> {
  if (data.length === 0) return {};
  const first = data[0];
  const firstPrice = priceValue(first.values);
  const secondPrice = priceValue(data[1]?.values);

  if (role === 'symbol') {
    const patch: Partial<CommoditiesTableRowType> = {
      date: first.transactionTime,
      last: firstPrice,
      periodAbs: first.values.PeriodAbs,
      metadata: first.metadata
    };
    if (firstPrice && secondPrice) {
      patch.delta = calculateDelta(firstPrice, secondPrice) ?? undefined;
    }
    return patch;
  }

  return {
    diff: firstPrice,
    diffPeriodAbs: first.values.PeriodAbs,
    diffDelta: firstPrice && secondPrice ? calculateDelta(firstPrice, secondPrice) : null,
    diffMetadata: first.metadata
  };
}

function enrichRow(row: RowType, dataRecord: GroupIndexDataRecord): RowType {
  let enriched = row;
  if (row.symbol.group !== DEFAULT_INDEX_GROUP && row.symbol.key.Code) {
    const data = dataRecord[symbolKey(row.symbol.key)];
    if (data) enriched = { ...enriched, ...enrichFromData(data, 'symbol') };
  }
  if (row.diffSymbol.group !== DEFAULT_INDEX_GROUP && row.diffSymbol.key.Code) {
    const data = dataRecord[symbolKey(row.diffSymbol.key)];
    if (data) enriched = { ...enriched, ...enrichFromData(data, 'diffSymbol') };
  }
  return enriched;
}

export function collectOtherGroupSymbols(
  sections: CommoditiesTableSectionType[] | undefined
): GroupIndexSymbol[] {
  return collectFetchableSymbols(sections, getRowSymbols);
}

export function hasOtherGroupSymbols(sections: CommoditiesTableSectionType[] | undefined): boolean {
  if (!sections || sections.length === 0) return false;
  return sections
    .flatMap(s => s.rows.flatMap(r => [r.symbol, r.diffSymbol]))
    .some(sym => sym?.key?.Code && sym.group !== DEFAULT_INDEX_GROUP);
}

export function useOtherGroupData(sections: CommoditiesTableSectionType[] | undefined): {
  sections: CommoditiesTableSectionType[] | undefined;
  isLoading: boolean;
} {
  return useEnrichedSections(sections, getRowSymbols, enrichRow);
}
